<template>
  <div>
    <el-card shadow="never">
      <div slot="header" class="card-header">
        <span>订单详情</span>
        <el-button @click="backToList">返回列表</el-button>
      </div>

      <el-alert
        v-if="loadError"
        class="state-block"
        title="订单详情加载失败"
        type="error"
        :closable="false"
        show-icon
      >
        <template slot>
          <span>可以检查订单号是否有效，或稍后重新加载。</span>
          <el-button type="text" @click="loadDetail">重新加载</el-button>
        </template>
      </el-alert>

      <div v-else v-loading="loading">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="订单号">{{ detail.orderNo || '-' }}</el-descriptions-item>
          <el-descriptions-item label="订单状态">{{ statusText(detail.orderStatus) }}</el-descriptions-item>
          <el-descriptions-item label="订单类型">
            <el-tag size="mini" :type="Number(detail.orderType) === 1 ? 'warning' : 'info'">
              {{ Number(detail.orderType) === 1 ? '团购单' : '普通单' }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="拼团编号">{{ detail.groupBuyRecordId || '-' }}</el-descriptions-item>
          <el-descriptions-item v-if="Number(detail.orderType) === 1" label="拼团状态">{{ groupBuyStatusText(detail.groupBuyStatus) }}</el-descriptions-item>
          <el-descriptions-item label="成团来源">{{ groupBuySuccessSourceText(detail.groupBuySuccessSource) }}</el-descriptions-item>
          <el-descriptions-item label="成团备注">{{ detail.groupBuySuccessRemark || '-' }}</el-descriptions-item>
          <el-descriptions-item label="支付状态">{{ detail.payStatus ? '已支付' : '未支付' }}</el-descriptions-item>
          <el-descriptions-item label="发货状态">{{ detail.deliveryStatus ? '已发货' : '未发货' }}</el-descriptions-item>
          <el-descriptions-item label="退款状态">
            <span v-if="!detail.refundStatus">无退款</span>
            <el-tag v-else :type="refundTagType(detail.refundStatus)">{{ refundText(detail.refundStatus) }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="退款单号">{{ detail.refundNo || '-' }}</el-descriptions-item>
          <el-descriptions-item label="关闭原因">{{ closeReasonText(detail.closeReason) }}</el-descriptions-item>
          <el-descriptions-item label="关闭备注">{{ detail.closeRemark || '-' }}</el-descriptions-item>
          <el-descriptions-item label="收货人">{{ detail.receiverName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="手机号">{{ detail.receiverMobile || '-' }}</el-descriptions-item>
          <el-descriptions-item label="订单金额">￥{{ formatPrice(detail.payAmount) }}</el-descriptions-item>
          <el-descriptions-item label="商品金额">￥{{ formatPrice(detail.totalAmount) }}</el-descriptions-item>
          <el-descriptions-item label="运费">￥{{ formatPrice(detail.freightAmount) }}</el-descriptions-item>
          <el-descriptions-item label="下单时间">{{ formatDateTime(detail.createTime) }}</el-descriptions-item>
          <el-descriptions-item label="支付时间">{{ formatDateTime(detail.paymentTime) }}</el-descriptions-item>
          <el-descriptions-item label="发货时间">{{ formatDateTime(detail.deliveryTime) }}</el-descriptions-item>
          <el-descriptions-item label="收货时间">{{ formatDateTime(detail.receiveTime) }}</el-descriptions-item>
          <el-descriptions-item label="退款时间">{{ formatDateTime(detail.refundTime) }}</el-descriptions-item>
          <el-descriptions-item label="收货地址" :span="2">{{ fullAddress }}</el-descriptions-item>
          <el-descriptions-item v-if="detail.buyerRemark" label="买家备注" :span="2">{{ detail.buyerRemark }}</el-descriptions-item>
          <el-descriptions-item v-if="detail.expressCompany" label="物流信息" :span="2">
            {{ detail.expressCompany }} {{ detail.expressNo }}
            <el-button v-if="detail.expressNo" type="text" @click="copyExpressNo">复制单号</el-button>
          </el-descriptions-item>
          <el-descriptions-item label="售后状态">{{ afterSaleText(detail.afterSaleStatus) }}</el-descriptions-item>
          <el-descriptions-item label="售后申请时间">{{ formatDateTime(detail.afterSaleApplyTime) }}</el-descriptions-item>
          <el-descriptions-item v-if="detail.afterSaleReason" label="售后原因" :span="2">{{ detail.afterSaleReason }}</el-descriptions-item>
          <el-descriptions-item v-if="detail.afterSaleRemark" label="处理备注" :span="2">{{ detail.afterSaleRemark }}</el-descriptions-item>
        </el-descriptions>
      </div>
    </el-card>

    <el-card v-if="!loadError" shadow="never" style="margin-top: 20px">
      <div slot="header">商品明细</div>
      <el-table :data="detail.items || []" border v-loading="loading">
        <el-table-column prop="productName" label="商品名称" />
        <el-table-column prop="skuName" label="SKU" />
        <el-table-column label="单价" width="120">
          <template slot-scope="{ row }">￥{{ formatPrice(row.price) }}</template>
        </el-table-column>
        <el-table-column prop="quantity" label="数量" width="100" />
      </el-table>
    </el-card>

    <el-card v-if="!loadError && detail.orderStatus === 1" shadow="never" style="margin-top: 20px">
      <div slot="header">订单发货</div>
      <el-alert
        v-if="!canDelivery"
        type="warning"
        :closable="false"
        show-icon
        class="state-block"
        title="该团购订单尚未拼团成功，不能发货。请先在拼团记录中提前成团、补满成团，或超时后标记失败退款。"
      />
      <template v-else>
        <el-form label-width="100px">
          <el-form-item label="快递公司"><el-input v-model.trim="delivery.expressCompany" /></el-form-item>
          <el-form-item label="物流单号"><el-input v-model.trim="delivery.expressNo" /></el-form-item>
          <el-form-item label="备注"><el-input v-model.trim="delivery.remark" /></el-form-item>
        </el-form>
        <el-button type="primary" :loading="deliverySubmitting" @click="submitDelivery">确认发货</el-button>
      </template>
    </el-card>

    <el-card v-if="!loadError && detail.afterSaleStatus" shadow="never" style="margin-top: 20px">
      <div slot="header">售后处理</div>
      <el-form label-width="100px">
        <el-form-item label="处理状态">
          <el-radio-group v-model="afterSaleForm.afterSaleStatus">
            <el-radio :label="1">已申请</el-radio>
            <el-radio :label="2">处理中</el-radio>
            <el-radio :label="3">已处理</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="处理备注">
          <el-input v-model.trim="afterSaleForm.afterSaleRemark" type="textarea" :rows="3" maxlength="255" show-word-limit />
        </el-form-item>
      </el-form>
      <el-button type="primary" :loading="afterSaleSubmitting" @click="submitAfterSale">保存售后处理</el-button>
    </el-card>
    <el-card v-if="!loadError && canShowRefundPanel" shadow="never" style="margin-top: 20px">
      <div slot="header">退款处理</div>
      <el-alert v-if="detail.refundNo" type="info" :closable="false" show-icon class="state-block">
        <template slot>
          <span>当前退款单号：{{ detail.refundNo }}，状态：{{ refundText(detail.refundStatus) }}</span>
        </template>
      </el-alert>
      <el-alert
        v-if="Number(detail.refundStatus) === 2"
        type="success"
        :closable="false"
        show-icon
        class="state-block"
        title="该订单已退款成功，不能重复发起退款或修改退款结果。"
      />
      <el-alert
        v-else-if="Number(detail.refundRecordStatus) === 1"
        type="warning"
        :closable="false"
        show-icon
        class="state-block"
        title="退款已进入处理中，请等待微信退款回调；如线下已处理，可人工标记结果。"
      />
      <el-form label-width="100px">
        <el-form-item label="退款原因">
          <el-input
            v-model.trim="refundForm.reason"
            type="textarea"
            :rows="3"
            maxlength="255"
            show-word-limit
            placeholder="例如：用户申请售后退款"
          />
        </el-form-item>
      </el-form>
      <el-button v-if="canCreateRefund" type="danger" :loading="refundSubmitting" @click="createRefund">
        {{ Number(detail.refundRecordStatus) === 3 ? '重新创建退款单' : '创建退款单' }}
      </el-button>
      <el-button v-if="canProcessRefund" type="danger" :loading="refundSubmitting" @click="processRefund">发起微信退款</el-button>
      <el-button v-if="canMarkRefundResult" :loading="refundSubmitting" @click="markRefundSuccess">标记退款成功</el-button>
      <el-button v-if="canMarkRefundResult" :loading="refundSubmitting" @click="markRefundFail">标记退款失败</el-button>
    </el-card>
  </div>
</template>

<script>
import {
  createOrderRefund,
  deliveryOrder,
  getOrderDetail,
  handleOrderAfterSale,
  markOrderRefundFail,
  markOrderRefundSuccess,
  processOrderRefund
} from '../../api/admin'
import { formatDateTime, formatPrice } from '../../utils/format'

export default {
  props: {
    orderNo: { type: String, required: true }
  },
  data() {
    return {
      loading: false,
      loadError: false,
      deliverySubmitting: false,
      afterSaleSubmitting: false,
      refundSubmitting: false,
      detail: { items: [] },
      delivery: { expressCompany: '', expressNo: '', remark: '' },
      afterSaleForm: { afterSaleStatus: 1, afterSaleRemark: '' },
      refundForm: { reason: '' }
    }
  },
  computed: {
    fullAddress() {
      const parts = [this.detail.receiverProvince, this.detail.receiverCity, this.detail.receiverDistrict, this.detail.receiverAddress].filter(Boolean)
      return parts.length ? parts.join('') : '-'
    },
    canDelivery() {
      return Number(this.detail.orderType) !== 1 || Number(this.detail.groupBuyStatus) === 1
    },
    canShowRefundPanel() {
      return Number(this.detail.payStatus) === 1 && Number(this.detail.orderStatus) !== 4
    },
    canCreateRefund() {
      if (!this.canShowRefundPanel || Number(this.detail.refundStatus) === 2) {
        return false
      }
      return !this.detail.refundRecordId || Number(this.detail.refundRecordStatus) === 3
    },
    canProcessRefund() {
      return this.canShowRefundPanel
        && this.detail.refundRecordId
        && Number(this.detail.refundRecordStatus) === 0
    },
    canMarkRefundResult() {
      return this.canShowRefundPanel
        && this.detail.refundRecordId
        && [0, 1].includes(Number(this.detail.refundRecordStatus))
    }
  },
  created() {
    this.loadDetail()
  },
  methods: {
    formatDateTime,
    formatPrice,
    statusText(status) {
      const map = { 0: '待付款', 1: '待发货', 2: '待收货', 3: '已完成', 4: '已取消' }
      return map[status] || '未知状态'
    },
    refundText(status) {
      const map = { 1: '退款中', 2: '退款成功', 3: '退款失败' }
      return map[status] || '无退款'
    },
    refundTagType(status) {
      const map = { 1: 'warning', 2: 'success', 3: 'danger' }
      return map[status] || 'info'
    },
    afterSaleText(status) {
      const map = { 0: '无售后', 1: '已申请', 2: '处理中', 3: '已处理' }
      return map[Number(status || 0)] || '无售后'
    },
    groupBuySuccessSourceText(source) {
      const map = {
        NATURAL: '自然成团',
        ADMIN_COMPLETE: '后台提前成团',
        ADMIN_FILL: '后台补满成团'
      }
      return map[source] || '-'
    },
    groupBuyStatusText(status) {
      const map = { 0: '拼团中', 1: '已成团', 2: '已关闭' }
      return map[Number(status)] || '-'
    },
    closeReasonText(reason) {
      const map = {
        USER_CANCEL: '用户取消',
        TIMEOUT: '超时关闭',
        GROUP_BUY_FAIL: '拼团失败',
        REFUND_SUCCESS: '退款关闭'
      }
      return map[reason] || reason || '-'
    },
    async loadDetail() {
      this.loading = true
      this.loadError = false
      try {
        this.detail = await getOrderDetail(this.orderNo)
        this.afterSaleForm = {
          afterSaleStatus: this.detail.afterSaleStatus || 1,
          afterSaleRemark: this.detail.afterSaleRemark || ''
        }
        this.refundForm.reason = this.detail.afterSaleReason || this.detail.closeRemark || '后台发起普通订单退款'
      } catch (error) {
        this.loadError = true
      } finally {
        this.loading = false
      }
    },
    async submitDelivery() {
      if (!this.delivery.expressCompany || !this.delivery.expressNo) {
        this.$message.error('请填写快递公司和物流单号')
        return
      }
      this.deliverySubmitting = true
      try {
        await deliveryOrder({
          orderNo: this.orderNo,
          expressCompany: this.delivery.expressCompany,
          expressNo: this.delivery.expressNo,
          remark: this.delivery.remark
        })
        this.$message.success('发货成功')
        this.delivery = { expressCompany: '', expressNo: '', remark: '' }
        this.backToList()
      } finally {
        this.deliverySubmitting = false
      }
    },
    async submitAfterSale() {
      this.afterSaleSubmitting = true
      try {
        await handleOrderAfterSale(this.orderNo, this.afterSaleForm)
        this.$message.success('售后处理已保存')
        this.loadDetail()
      } finally {
        this.afterSaleSubmitting = false
      }
    },
    createRefund() {
      this.$confirm(`确认为该订单创建退款单吗？退款金额将按订单实付金额 ￥${this.formatPrice(this.detail.payAmount)} 处理。`, '创建退款单', {
        type: 'warning'
      }).then(async () => {
        this.refundSubmitting = true
        try {
          const data = await createOrderRefund(this.orderNo, { reason: this.refundForm.reason })
          this.$message.success(`退款单已创建：${data.refundNo}`)
          await this.loadDetail()
        } finally {
          this.refundSubmitting = false
        }
      }).catch(() => {})
    },
    processRefund() {
      this.$confirm(`确认发起退款单 ${this.detail.refundNo} 吗？`, '发起微信退款', {
        type: 'warning'
      }).then(async () => {
        this.refundSubmitting = true
        try {
          await processOrderRefund(this.detail.refundRecordId)
          this.$message.success('退款已提交处理')
          await this.loadDetail()
        } finally {
          this.refundSubmitting = false
        }
      }).catch(() => {})
    },
    markRefundSuccess() {
      this.$prompt('可选填写备注', '标记退款成功', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputPlaceholder: '例如：线下已退款'
      }).then(async ({ value }) => {
        this.refundSubmitting = true
        try {
          await markOrderRefundSuccess(this.detail.refundRecordId, { remark: value || '' })
          this.$message.success('已标记为退款成功')
          await this.loadDetail()
        } finally {
          this.refundSubmitting = false
        }
      }).catch(() => {})
    },
    markRefundFail() {
      this.$prompt('请填写失败原因', '标记退款失败', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputPlaceholder: '例如：退款配置未完成'
      }).then(async ({ value }) => {
        this.refundSubmitting = true
        try {
          await markOrderRefundFail(this.detail.refundRecordId, { remark: value || '' })
          this.$message.success('已标记为退款失败')
          await this.loadDetail()
        } finally {
          this.refundSubmitting = false
        }
      }).catch(() => {})
    },
    async copyExpressNo() {
      try {
        await navigator.clipboard.writeText(this.detail.expressNo)
        this.$message.success('物流单号已复制')
      } catch (error) {
        this.$message.warning('复制失败，请手动复制')
      }
    },
    backToList() {
      this.$router.push({ path: '/orders', query: this.$route.query.q ? { q: this.$route.query.q } : {} })
    }
  }
}
</script>

<style scoped>
.card-header { display: flex; justify-content: space-between; align-items: center; }
.state-block { margin-bottom: 16px; }
</style>
